import { DeleteOutlined, EditOutlined } from "@ant-design/icons"
import { SearchBar, StyledPaginationComponent, Table } from "../../atom"
import { Header } from "../Header"
import { getKeyword, setKeyword, DASHBOARD_TYPE } from "../../../utils"
import { theme } from "../../../theme"
import { useRouter } from "next/router"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { notification, Popconfirm } from "antd"
import { useState, useEffect } from "react"
import { useMutation, useQuery } from "react-query"
import { deleteProductList, fetchProductList } from "../../../services"
import { useDebounce } from "../UseDebounce"

const Container = styled.div`
  background-color: ${theme.background.colorSecondary3};
  .container {
    margin: 35px;
    border: solid 1px ${theme.border.colorSecondary1};
    padding: 20px;
    border-radius: 5px;
    background: ${theme.background.colorSecondary1};
  }
  .search-bar {
    margin-bottom: 25px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .ant-popover > .ant-popover-content {
    width: 334px !important;
    height: 153px;
  }
  .ant-input-search .ant-input-group .ant-input-affix-wrapper:not(:last-child) {
    border-radius: 5px 0px 0px 5px;
    border: 1px solid #8c8989;
    background: #fff;
    height: 42px;
  }
  .ant-input-search
    > .ant-input-group
    > .ant-input-group-addon:last-child
    .ant-input-search-button {
    padding-top: 0;
    padding-bottom: 0;
    border-radius: 0px 0px 0px 0px;
    border: 1px solid #8c8989;
    background: #fff;
  }
`
const PopoverTitle = styled.div`
  font-family: "Noto Sans JP", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: left;
`

const PopoverButton = styled.div`
  font-family: "Noto Sans JP", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: center;
`

export interface ProductListProps {
  page: string | number
  query?: string
  facilityId?: string | null
  dashboardType?: DASHBOARD_TYPE
}

const ProductList: React.FC<ProductListProps> = ({
  page,
  query = "",
  facilityId,
  dashboardType = DASHBOARD_TYPE.BUSINESS,
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const keyword = query !== "" ? query : getKeyword(router.pathname)
  const limit = 10
  const [searchQuery, setSearchQuery] = useState(
    keyword ?? getKeyword(router.pathname)
  )
  const debounceSearchQ = useDebounce(searchQuery, 500)

  useEffect(() => {
    setKeyword(router.pathname, debounceSearchQ)
    if (debounceSearchQ !== keyword) {
      handlePageChange(1)
    } else if (keyword) {
      handlePageChange(page)
    }
  }, [debounceSearchQ])

  useEffect(() => {
    setSearchQuery(keyword)
  }, [keyword])

  const handlePageChange = (nextPage) => {
    const currentKeyword = unescape(getKeyword(router.pathname))
    router.push(
      `${router.pathname}?page=${nextPage}${
        currentKeyword ? "&keyword=" + currentKeyword : ""
      }`
    )
  }

  const { isLoading, isFetching, data, refetch } = useQuery(
    ["product-list", page, limit, debounceSearchQ, facilityId],
    fetchProductList,
    {
      refetchOnWindowFocus: false,
      enabled: !!facilityId,
      select: (response) => {
        return {
          data: response?.data?.map((item) => {
            return {
              key: item.id,
              id: item.id,
              category: item.product_category?.name || "-",
              name: item.name || "-",
              listing_price: item.regular_price?.toLocaleString() || "-",
              selling_price: item.selling_price?.toLocaleString() || "-",
              facility_id: item.facility_id || "-",
              long_description: item.long_description || "-",
              is_deleted: item.is_deleted || "-",
              product_category_id: item.product_category_id || "-",
              short_description: item.short_description || "-",
            }
          }),
          count: response?.total_count,
        }
      },
    }
  )

  const { mutate } = useMutation("product", deleteProductList, {
    onSuccess: () => {
      notification.success({ message: t("Successfully Deleted Products") })
      refetch()
    },
    onError: () => {
      notification.error({
        message: `${t("Something Went Wrong while Deleting Product")}`,
      })
    },
  })

  const columns = [
    {
      title: t("Category"),
      dataIndex: "category",
      key: "category",
      align: "center",
    },
    {
      title: t("Product name"),
      dataIndex: "name",
      key: "name",
      align: "center",
    },
    {
      title: t("Listing Price"),
      dataIndex: "listing_price",
      key: "listing_price",
      width: "200px",
      align: "center",
      render: (price: string | number) => {
        return <span>{`¥${price}`}</span>
      },
    },
    {
      title: t("Selling Price"),
      dataIndex: "selling_price",
      key: "selling_price",
      width: "200px",
      align: "center",
      render: (price: string | number) => {
        return <span>{`¥${price}`}</span>
      },
    },
    {
      title: t("Action"),
      dataIndex: "id",
      key: "id",
      width: "200px",
      align: "center",
      render: (id) => (
        <div className={"action-icons"}>
          <a
            type={"link"}
            onClick={() => router.push(`/product/edit/${id}`)}
            className={"link-active"}
          >
            <EditOutlined />
          </a>
          <Popconfirm
            placement={"bottomRight"}
            overlayStyle={{ width: "334px", height: "153px" }}
            title={
              <PopoverTitle>
                {t("Are you sure you want to delete this product ?")}
              </PopoverTitle>
            }
            onConfirm={() => {
              mutate(id)
            }}
            okText={<PopoverButton>{t("Yes")}</PopoverButton>}
            cancelText={t("No")}
            icon={null}
            okButtonProps={{
              style: {
                background: "green",
                color: "white",
                width: "80px",
                height: "38px",
                backgroundColor: "#1B6335",
                borderRadius: "100px",
                padding: "8px, 16px, 8px, 16px",
              },
            }}
            cancelButtonProps={{
              style: {
                width: "80px",
                height: "38px",
                borderRadius: "100px",
                padding: "8px, 16px, 8px, 16px",
                border: " 1px solid #666666",
              },
            }}
          >
            <DeleteOutlined />
          </Popconfirm>
        </div>
      ),
    },
  ]
  return (
    <Container>
      <Header
        title={t("Product management")}
        add
        addLabel={t("Add Product")}
        onClick={() => router.push("/product/add")}
      />
      <div className={"container"}>
        <div className={"search-bar"}>
          <SearchBar
            value={searchQuery}
            onChange={(val) => {
              setSearchQuery(val.target.value)
            }}
            onSearch={() => {
              refetch()
            }}
            width={250}
            placeholder={t("Search by product name")}
          />
        </div>
        <Table
          type={dashboardType}
          dataSource={data?.data}
          columns={columns}
          pagination={false}
          loading={isLoading || isFetching}
        />
        <StyledPaginationComponent
          type={dashboardType}
          current={+page}
          hideOnSinglePage={false}
          total={data?.count ?? 0}
          showSizeChanger={false}
          pageSize={limit}
          onChange={handlePageChange}
        />
      </div>
    </Container>
  )
}

export { ProductList }
